import * as React from 'react';

import { cn } from '@/lib/utils';

import { CutOut } from './CutOut';

/**
 * Card — scissors-cut paper card.
 * Wraps content in a CutOut for the jagged-edge effect.
 * Pass `cut={false}` to disable the clip-path (e.g. for nested cards).
 * Pass `cutVariant={0-3}` to pick a specific scissors pattern.
 */
const Card = React.forwardRef(({ className, cut = true, cutVariant = 0, ...props }, ref) => {
  if (cut) {
    return (
      <CutOut
        ref={ref}
        variant={cutVariant}
        className={cn('bg-[#fefcf6] text-[#1a1a1a]', className)}
        {...props}
      />
    );
  }

  return (
    <div
      ref={ref}
      className={cn(
        'bg-[#fefcf6] border-2 border-[#1a1a1a] shadow-[4px_4px_0px_#1a1a1a] text-[#1a1a1a]',
        className
      )}
      {...props}
    />
  );
});
Card.displayName = 'Card';

const CardHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('flex flex-col space-y-1.5 p-6', className)} {...props} />
));
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn('text-xl font-semibold leading-none tracking-tight', className)}
    style={{ fontFamily: "'Permanent Marker', cursive" }}
    {...props}
  />
));
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm', className)}
    style={{ fontFamily: "'Patrick Hand', cursive", color: '#6b5f4d' }}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
));
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('flex items-center p-6 pt-0', className)} {...props} />
));
CardFooter.displayName = 'CardFooter';

export { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle };
export default Card;
